import React, { useState, useEffect } from 'react';
import { BarChart3, PieChart, Skull } from 'lucide-react';
import * as statsApi from '../../api/stats';
import './gameDetails.css';

const CHART_COLORS = [
  '#a855f7', '#00f5ff', '#00ff87', '#ff9800', '#ff00ff',
  '#ffd700', '#ff1744', '#4dabf7', '#82c91e', '#e64980',
];

/**
 * Post-deadline round analytics: who everyone backed this week, and once results are in,
 * which teams did the most damage.
 *
 * Both endpoints 409 until the week reaches the required state, so each section decides
 * independently whether it has anything to show - a locked-but-not-closed week renders the
 * pick charts alone without erroring.
 */
export default function RoundAnalytics({ gameId, gameWeek }) {
  const [pickStats, setPickStats] = useState(null);
  const [eliminationStats, setEliminationStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const [picks, eliminations] = await Promise.all([
        fetchStats(() => statsApi.getPickStats(gameId, gameWeek)),
        fetchStats(() => statsApi.getEliminationStats(gameId, gameWeek)),
      ]);
      if (cancelled) return;
      setPickStats(picks);
      setEliminationStats(eliminations);
      setLoading(false);
    };

    load();
    return () => { cancelled = true; };
  }, [gameId, gameWeek]);

  if (loading) {
    return <div className="ra-placeholder">Loading round stats...</div>;
  }

  if (!pickStats && !eliminationStats) {
    return (
      <div className="ra-placeholder">
        Round stats unlock once picks are locked for this gameweek.
      </div>
    );
  }

  return (
    <div className="ra-stack">
      {eliminationStats && eliminationStats.topLosers.length > 0 && (
        <Card icon={<Skull size={18} />} title="Biggest Losers"
              subtitle="Teams that knocked out the most players this week">
          <LosersList losers={eliminationStats.topLosers} />
        </Card>
      )}

      {pickStats && (
        <>
          {/*
            The percentages here are each team's share of the picks that were cast, which
            is what makes the pie add up. That is deliberately not the same measure as
            "34% of players backed City", which divides by everyone who could pick — so
            the subtitles say which one this is rather than leaving a reader to assume.
          */}
          <Card icon={<BarChart3 size={18} />} title="Most Picked — Everywhere"
                subtitle="Share of every pick cast across this tournament">
            <PickBarChart stats={pickStats.globalStats} />
          </Card>

          <Card icon={<PieChart size={18} />} title="Picks In Your Game"
                subtitle="Share of the picks made in your game">
            <PickPieChart stats={pickStats.gameStats} />
          </Card>
        </>
      )}
    </div>
  );
}

// Both endpoints 409 until the week reaches the required state; treat any
// failure as "nothing to show yet" (was: non-ok returned null).
async function fetchStats(load) {
  try {
    return await load();
  } catch (err) {
    if (err.name !== 'ApiError') console.error('Error loading round stats:', err);
    return null;
  }
}

function Card({ icon, title, subtitle, children }) {
  return (
    <div className="ra-card">
      <div className="ra-card-header">
        <span className="ra-card-icon">{icon}</span>
        <div>
          <div className="ra-card-title">{title}</div>
          <div className="ra-card-subtitle">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function LosersList({ losers }) {
  const max = Math.max(...losers.map(l => l.eliminationCount));

  return (
    <div className="ra-rows">
      {losers.map((loser, i) => (
        <div key={loser.teamId} className="ra-row">
          <span className="ra-rank">{i + 1}</span>
          <span className="ra-label is-loser">{loser.teamName}</span>
          <div className="ra-track is-loser">
            <div
              className="ra-fill is-loser"
              style={{ width: `${(loser.eliminationCount / max) * 100}%` }}
            />
          </div>
          <span className="ra-value is-loser">
            {loser.eliminationCount} {loser.eliminationCount === 1 ? 'player' : 'players'}
          </span>
        </div>
      ))}
    </div>
  );
}

function PickBarChart({ stats }) {
  if (!stats || stats.length === 0) {
    return <div className="ra-empty">No picks recorded.</div>;
  }

  const shown = stats.slice(0, 10);
  const max = Math.max(...shown.map(s => s.pickCount));

  return (
    <div className="ra-rows is-tight">
      {shown.map((stat, i) => (
        <div key={stat.teamId} className="ra-row">
          <span className="ra-label" title={stat.teamName}>{stat.teamName}</span>
          <div className="ra-track">
            <div
              className="ra-fill"
              style={{
                width: `${(stat.pickCount / max) * 100}%`,
                background: CHART_COLORS[i % CHART_COLORS.length],
              }}
            />
          </div>
          <span className="ra-value">
            {stat.shareOfPicks.toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  );
}

function PickPieChart({ stats }) {
  if (!stats || stats.length === 0) {
    return <div className="ra-empty">No picks recorded in your game.</div>;
  }

  const radius = 70;
  const centre = 80;
  let cursor = 0;

  const slices = stats.map((stat, i) => {
    const slice = {
      ...stat,
      color: CHART_COLORS[i % CHART_COLORS.length],
      path: describeSlice(centre, radius, cursor, cursor + stat.shareOfPicks),
    };
    cursor += stat.shareOfPicks;
    return slice;
  });

  return (
    <div className="ra-pie-wrap">
      <svg width="160" height="160" viewBox="0 0 160 160" className="ra-pie">
        {/* A lone pick would produce a degenerate 100% arc, so draw it as a full circle */}
        {slices.length === 1 ? (
          <circle cx={centre} cy={centre} r={radius} fill={slices[0].color} />
        ) : (
          slices.map(slice => (
            <path key={slice.teamId} d={slice.path} fill={slice.color}>
              <title>{`${slice.teamName}: ${slice.pickCount} (${slice.shareOfPicks.toFixed(1)}%)`}</title>
            </path>
          ))
        )}
      </svg>

      <div className="ra-legend">
        {slices.map(slice => (
          <div key={slice.teamId} className="ra-legend-row">
            <span className="ra-swatch" style={{ background: slice.color }} />
            <span className="ra-legend-name">{slice.teamName}</span>
            <span className="ra-legend-value">
              {slice.pickCount} · {slice.shareOfPicks.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function describeSlice(centre, radius, startPercent, endPercent) {
  const start = polarPoint(centre, radius, startPercent);
  const end = polarPoint(centre, radius, endPercent);
  const largeArc = endPercent - startPercent > 50 ? 1 : 0;
  return `M ${centre} ${centre} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y} Z`;
}

function polarPoint(centre, radius, percent) {
  const angle = (percent / 100) * 2 * Math.PI - Math.PI / 2;
  return {
    x: centre + radius * Math.cos(angle),
    y: centre + radius * Math.sin(angle),
  };
}

